import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Mail } from 'lucide-react';
import { AuthHeader, AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';

/**
 * Reached when the API's access gate rejects an authenticated
 * email — surfaced as `api.auth.organization_membership_required`
 * when the user authenticated through WorkOS but isn't an active
 * member of the configured Fluxter WorkOS Organization. The
 * user-facing meaning is "you got through Google/WorkOS, but
 * you're not invited yet."
 *
 * Routed here from `AuthCallbackPage`, `CodeSentPage`, and the
 * desktop `LoginPage` when the API call throws that error_code.
 *
 * The route is publicly addressable. If someone bookmarks or links
 * directly here without going through sign-in, they see the same
 * polished message — informative rather than misleading. No state
 * leaks (we don't display the rejected email; renderer never had it
 * persisted in the first place because no token was issued).
 */
const CONTACT_EMAIL = 'team@13bytes.co';

export const AccessDeniedPage = () => {
  const navigate = useNavigate();

  return (
    <AuthShell
      width="md"
      header={
        <AuthHeader
          title="You're a little early"
          subtitle="Fluxter is in private beta while we shape it with a small group of design partners."
        />
      }
    >
      <div className="space-y-5">
        <p className="text-foreground-muted text-[13px] leading-relaxed">
          Sign-in worked, but the email you used isn&apos;t on the beta access list yet. We&apos;ll
          be opening things up to more teams soon — if you&apos;d like an early invite, drop us a
          note and we&apos;ll get back to you.
        </p>

        <a
          href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Fluxter beta access request')}`}
          className="border-border bg-card hover:bg-accent/40 flex items-center justify-between rounded-lg border px-4 py-3 transition-colors"
        >
          <span className="flex items-center gap-2.5">
            <Mail className="text-foreground-muted size-4" aria-hidden="true" />
            <span className="text-foreground text-[13px] font-medium">{CONTACT_EMAIL}</span>
          </span>
          <span className="text-foreground-faint text-[11px]">Request access</span>
        </a>

        <div className="border-border border-t pt-5">
          <Button
            type="button"
            variant="outline"
            onClick={() => void navigate({ to: '/login' })}
            className="w-full"
          >
            <ArrowLeft className="mr-1.5 size-3.5" aria-hidden="true" />
            Try a different account
          </Button>
          <p className="text-foreground-faint mt-3 text-center text-[11px]">
            Already on the beta? Sign out of Google in your browser, then try again.
          </p>
        </div>
      </div>
    </AuthShell>
  );
};
